import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, Settings, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { primaryText, secondaryText, smallTextSize, mediumText } from '../../../styling/stylingHelper';
import { useAuth } from '../../../services/authentication/authContext';
import { canAccessMenuChangeDashboard } from '../../../services/accessManagement/accessManagement';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useMenuChangeStore } from '../../../stores/appMenuChangeStore';
import { createRollout as apiCreateRollout } from '../../../services/menuChange/menuChangeService';

const MenuChangeActions: React.FC<{ restaurantId?: string }> = ({ restaurantId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { backendUser, accountSettings } = useAuth();
  const canManage = canAccessMenuChangeDashboard(backendUser, accountSettings);
  const restaurants = useRestaurantStore(s => s.restaurants);
  const templates = useMenuChangeStore(s => s.templates);
  const loadTemplates = useMenuChangeStore(s => s.loadTemplates);
  const loadDashboard = useMenuChangeStore(s => s.loadDashboard);
  const loadDetail = useMenuChangeStore(s => s.loadDetail);
  const [showNewModal, setShowNewModal] = useState(false);

  useEffect(() => {
    if (canManage) {
      loadTemplates();
    }
  }, [canManage, loadTemplates]);

  if (!canManage) return null;

  return (
    <>
      <div className="flex max-lg:flex-wrap items-center gap-3">
        <button
          onClick={() => navigate('/menus/change-setup')}
          className="flex items-center justify-center gap-2 text-nowrap h-11 px-3 rounded-xl bg-white dark:bg-[#14344C] border border-[#D0D5DD] dark:border-[#2D4F68] shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040] hover:bg-[#F9FAFB] dark:hover:bg-[#14344C]/80 transition-colors"
        >
          <Settings className="w-5 h-5 text-[#344054] dark:text-[#CBD5E1]" />
          <span className="text-[14px] leading-5 font-semibold text-[#344054] dark:text-[#CBD5E1]">{t('menuChange.setup')}</span>
        </button>

        <button
          onClick={() => setShowNewModal(true)}
          className="flex items-center justify-center gap-2 text-nowrap h-11 px-[13.5px] rounded-xl bg-[#0E8965] dark:bg-[#0E8965] text-white dark:text-white hover:bg-[#047857] transition-colors flex-shrink-0"
        >
          <Plus className="w-5 h-5" />
          <span className="text-[16px] leading-6 font-semibold text-white dark:text-white">{t('menuChange.newMenuChange')}</span>
        </button>
      </div>
      {showNewModal && (
        <NewMenuChangeModal
          restaurants={restaurants.filter(r => !restaurantId || r.id === restaurantId).map((r) => ({ id: r.id, name: r.name }))}
          templates={templates.map((tpl) => ({ id: tpl.id, name: tpl.name }))}
          onClose={() => setShowNewModal(false)}
          onCreated={(restaurantId) => {
            setShowNewModal(false);
            loadDashboard(true);
            loadDetail(restaurantId, true);
          }}
        />
      )}
    </>
  );
};

interface NewMenuChangeModalProps {
  restaurants: { id: string; name: string }[];
  templates: { id: string; name: string }[];
  onClose: () => void;
  onCreated: (restaurantId: string) => void;
}

const NewMenuChangeModal: React.FC<NewMenuChangeModalProps> = ({ restaurants, templates, onClose, onCreated }) => {
  const { t } = useTranslation();
  const [restaurantId, setRestaurantId] = useState(restaurants.length === 1 ? restaurants[0].id : '');
  const [templateId, setTemplateId] = useState('');
  const [name, setName] = useState('');
  const [goLiveDate, setGoLiveDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = restaurantId && templateId && name.trim() && goLiveDate;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSaving(true);
    setError('');
    try {
      await apiCreateRollout({ restaurantId, templateId, name: name.trim(), goLiveDate });
      onCreated(restaurantId);
    } catch (err) {
      console.error('Failed to create rollout:', err);
      setError(t('menuChange.createError'));
    } finally {
      setIsSaving(false);
    }
  };

  const fieldClass = 'w-full px-3 py-2 rounded-xl border border-[#D0D5DD] dark:border-[#2D4F68] bg-white dark:bg-zinc-800 text-[#101828] dark:text-[#F8FAFC] outline-none focus:ring-2 focus:ring-[#0E8965]';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/50" onClick={onClose}>
      <div className="bg-white dark:bg-[#0E2B42] rounded-2xl shadow-xl w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className={cn('text-lg font-semibold', primaryText)}>{t('menuChange.newMenuChange')}</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={cn('block mb-1', smallTextSize, mediumText, secondaryText)}>{t('menuChange.restaurant')}</label>
            <select value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)} className={fieldClass}>
              <option value="">{t('menuChange.selectRestaurant')}</option>
              {restaurants.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={cn('block mb-1', smallTextSize, mediumText, secondaryText)}>{t('menuChange.template')}</label>
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className={fieldClass}>
              <option value="">{t('menuChange.selectTemplate')}</option>
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={cn('block mb-1', smallTextSize, mediumText, secondaryText)}>{t('menuChange.name')}</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('menuChange.namePlaceholder')} className={fieldClass} />
          </div>

          <div>
            <label className={cn('block mb-1', smallTextSize, mediumText, secondaryText)}>{t('menuChange.goLiveDate')}</label>
            <input type="date" value={goLiveDate} onChange={(e) => setGoLiveDate(e.target.value)} className={fieldClass} />
          </div>

          {error && <p className="text-red-500 dark:text-textRed-dark text-sm">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 rounded-xl hover:bg-zinc-200 dark:hover:bg-zinc-700">
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={!canSubmit || isSaving}
              className="px-4 py-2 text-sm font-medium text-white dark:text-white bg-[#0E8965] dark:bg-[#0E8965] rounded-xl hover:bg-[#047857] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? t('common.saving') : t('common.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MenuChangeActions;
